import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Assessment, Book, CalendarMonth, Campaign, Dashboard,
  HelpOutline, Insights, LocalOffer, Menu, Message, Notifications, People, PersonSearch,
  RateReview, School, Settings, ShieldOutlined, UploadFile,
  Videocam, Visibility,
} from '@mui/icons-material';
import '../styles/layout.css';
import { AuthContext } from '../auth/AuthContext';
import { logout as performLogout } from '../services/auth';
import api from '../services/api';
import AdminPageHeader from './AdminPageHeader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NAVIGATION = [
  { label: 'Overview', items: [{ label: 'Dashboard', to: '/admin', icon: Dashboard }] },
  { label: 'People', items: [
    { label: 'Users', to: '/admin/users', icon: People },
    { label: 'Tutor Imports', to: '/admin/tutor-imports', icon: UploadFile },
    { label: 'Reviews', to: '/admin/reviews', icon: RateReview },
  ] },
  { label: 'Academic', items: [
    { label: 'Math Classes', to: '/admin/math-classes', icon: Book, count: 'classes' },
    { label: 'Matric Classes', to: '/admin/matric-classes', icon: School, count: 'classes' },
    { label: 'Subjects', to: '/admin/subjects', icon: PersonSearch },
    { label: 'Recordings', to: '/admin/recordings', icon: Videocam },
  ] },
  { label: 'Operations', items: [
    { label: 'Bookings', to: '/admin/bookings', icon: CalendarMonth },
    { label: 'Discount Codes', to: '/admin/discount-codes', icon: LocalOffer },
    { label: 'Lesson Alerts', to: '/admin/lesson-alerts', icon: Notifications, count: 'lessonAlerts' },
    { label: 'Announcements', to: '/admin/announcements', icon: Campaign, count: 'announcements' },
    { label: 'Reports', to: '/admin/reports', icon: Assessment },
  ] },
  { label: 'Communication', items: [
    { label: 'Messages', to: '/admin/messages', icon: Message, count: 'messages' },
    { label: 'Message Oversight', to: '/admin/message-oversight', icon: Visibility },
    { label: 'Support', to: '/admin/support', icon: HelpOutline },
    { label: 'Notifications', to: '/admin/notifications', icon: Notifications },
  ] },
  { label: 'Admin', items: [
    { label: 'Analytics', to: '/admin/analytics', icon: Insights },
    { label: 'Settings', to: '/admin/settings', icon: Settings },
  ] },
];

const DashboardLayout = ({ children }) => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [counts, setCounts] = useState({ messages: 0, announcements: 0, classes: 0, lessonAlerts: 0 });
  const { token: contextToken, user: currentUser, setToken, setUser, setRole } = useContext(AuthContext) ?? {};
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;

  const displayName = useMemo(() => currentUser?.name || 'Admin', [currentUser?.name]);
  const token = contextToken || null;
  const lastSeenKey = useCallback(
    (category) => `lms:lastSeen:admin:${category}:${currentUser?._id || 'anon'}`,
    [currentUser?._id],
  );
  const getLastSeen = useCallback((category) => {
    try {
      return Number(localStorage.getItem(lastSeenKey(category))) || 0;
    } catch {
      return 0;
    }
  }, [lastSeenKey]);
  const setLastSeen = useCallback((category) => {
    try {
      localStorage.setItem(lastSeenKey(category), String(Date.now()));
    } catch {}
  }, [lastSeenKey]);
  const countNewerThan = useCallback((items, lastSeen) => items.reduce((count, item) => {
    const timestamp = new Date(item?.createdAt || item?.startTime || item?.updatedAt || 0).getTime();
    return timestamp > lastSeen ? count + 1 : count;
  }, 0), []);

  const handleLogout = () => {
    setMobileOpen(false);
    setToken?.(null); setUser?.(null); setRole?.(null);
    performLogout();
  };

  const refreshNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const [unreadRes, announcementRes, mathRes, matricRes, lessonAlertRes] = await Promise.all([
        api.get('/messages/unread-counts').catch(() => ({ data: {} })),
        api.get('/announcements?limit=20').catch(() => ({ data: [] })),
        api.get('/session', { params: { program: 'math' } }).catch(() => ({ data: [] })),
        api.get('/session', { params: { program: 'matric' } }).catch(() => ({ data: [] })),
        api.get('/lesson-alerts/admin/summary').catch(() => ({ data: {} })),
      ]);
      const messages = Object.values(unreadRes?.data || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
      const announcements = Array.isArray(announcementRes?.data?.items)
        ? announcementRes.data.items
        : (Array.isArray(announcementRes?.data) ? announcementRes.data : []);
      const mathClasses = Array.isArray(mathRes?.data?.items)
        ? mathRes.data.items
        : (Array.isArray(mathRes?.data) ? mathRes.data : []);
      const matricClasses = Array.isArray(matricRes?.data?.items)
        ? matricRes.data.items
        : (Array.isArray(matricRes?.data) ? matricRes.data : []);
      setCounts({
        messages,
        announcements: countNewerThan(announcements, getLastSeen('announcements')),
        classes: countNewerThan([...mathClasses, ...matricClasses], getLastSeen('classes')),
        lessonAlerts: Number(lessonAlertRes?.data?.totalOpen || 0),
      });
    } catch {}
  }, [countNewerThan, getLastSeen, token]);

  useEffect(() => {
    if (!token) return undefined;
    refreshNotifications();
    const intervalId = setInterval(refreshNotifications, 40000);
    return () => clearInterval(intervalId);
  }, [refreshNotifications, token]);

  useEffect(() => {
    if (!token) return undefined;
    let eventSource;
    let cancelled = false;
    const connect = async () => {
      try {
        const { data } = await api.post('/messages/stream-token');
        if (cancelled || !data?.token) return;
        const base = process.env.REACT_APP_API_URL || api.defaults?.baseURL || '';
        eventSource = new EventSource(
          `${base.replace(/\/$/, '')}/messages/stream?token=${encodeURIComponent(data.token)}`,
        );
        eventSource.addEventListener('message', (event) => {
          try {
            const payload = JSON.parse(event.data || '{}');
            if (payload?.type === 'new-message') refreshNotifications();
          } catch {}
        });
      } catch {}
    };
    connect();
    return () => {
      cancelled = true;
      eventSource?.close();
    };
  }, [refreshNotifications, token]);

  useEffect(() => {
    const timers = [];
    const clearAfterViewing = (category, matchesRoute) => {
      if (!matchesRoute) return;
      timers.push(setTimeout(() => {
        setLastSeen(category);
        setCounts((current) => ({ ...current, [category]: 0 }));
      }, 3000));
    };
    clearAfterViewing('messages', pathname.startsWith('/admin/messages'));
    clearAfterViewing('announcements', pathname.startsWith('/admin/announcements'));
    if (pathname.startsWith('/admin/math-classes') || pathname.startsWith('/admin/matric-classes')) {
      timers.push(setTimeout(() => {
        setLastSeen('classes');
        setCounts((d) => ({ ...d, classes: 0 }));
      }, 3000));
    }
    return () => timers.forEach(clearTimeout);
  }, [pathname, setLastSeen]);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = pathname === item.to || (item.to !== '/admin' && pathname.startsWith(`${item.to}/`));
    const value = item.count ? counts[item.count] : 0;
    return (
      <li key={item.to} className={active ? 'active' : ''}>
        <Link to={item.to} aria-current={active ? 'page' : undefined}>
          <Icon className="nav-icon" fontSize="small" />
          <span>{item.label}</span>
          {value > 0 && <span className="badge">{value > 99 ? '99+' : value}</span>}
        </Link>
      </li>
    );
  };

  return (
    <div className={`dashboard-layout ${mobileOpen ? 'open' : ''}`}>
      {mobileOpen && <button className="sidebar-overlay" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
      <aside className="sidebar" aria-label="Admin navigation">
        <Link className="admin-brand" to="/admin">
          <img src="/logo.png" alt="Apex Academic Centre" />
          <span><strong>MY APEX</strong><small>ADMIN PORTAL</small></span>
        </Link>
        <nav>{NAVIGATION.map((group) => (
          <section className="nav-group" key={group.label}>
            <h2>{group.label}</h2><ul>{group.items.map(renderNavItem)}</ul>
          </section>
        ))}</nav>
        <div className="sidebar-footer"><ShieldOutlined fontSize="small" /> <span>Secure administrator area</span></div>
      </aside>
      <main className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            {isMobile && <button className="mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open menu"><Menu /></button>}
            <div><p className="topbar-eyebrow">APEX ACADEMIC CENTRE</p><p className="topbar-context">Administration workspace</p></div>
          </div>
          <div className="topbar-right">
            <span className="admin-avatar">{displayName.slice(0, 1).toUpperCase()}</span>
            <span className="welcome-text">{displayName}</span>
            <button type="button" className="logout-button" onClick={handleLogout}>Log out</button>
          </div>
        </header>
        <section className="content kit-content" data-route={pathname}>
          <AdminPageHeader />
          <div className="admin-page-content">{children}</div>
        </section>
      </main>
      <ToastContainer position="top-right" autoClose={6000} newestOnTop closeOnClick pauseOnHover draggable={false} theme="colored" className="z-toast" />
    </div>
  );
};

export default DashboardLayout;
